import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {AceLogger} from '@app/logging/ace-logger';
import {AceLoggerService} from '@app/logging/ace-logger.service';

interface IconLink {
  rel: string;
  href: string;
  sizes: string | null;
}

@Injectable({providedIn: 'root'})
export class FaviconService {
  private readonly logger: AceLogger;
  private readonly BLANK_ICON_LINK_SELECTOR = 'link[rel="icon"][href="data:,"]';
  private readonly ICON_LINKS: IconLink[] = [
    {rel: 'icon', href: '/assets/images/favicon/favicon-32x32.png', sizes: '32x32'},
    {rel: 'icon', href: '/assets/images/favicon/favicon-192x192.png', sizes: '192x192'},
    {rel: 'apple-touch-icon-precomposed', href: '/assets/images/favicon/favicon-180x180.png', sizes: null}
  ];
  private readonly TILE_IMAGE_URL = '/assets/images/favicon/favicon-270x270.png';

  private renderer: Renderer2;

  constructor(rendererFactory: RendererFactory2, logService: AceLoggerService) {
    this.logger = logService.create(FaviconService.name);
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  addFavicon(): void {
    if (document.head) {
      const blankIconLink = document.head.querySelector(this.BLANK_ICON_LINK_SELECTOR);
      if (blankIconLink) {
        const faviconNodes = [
          ...this.ICON_LINKS.map(iconLink => this.makeIconLink(iconLink)),
          this.makeTileImageMeta()
        ];
        this.renderer.removeChild(document.head, blankIconLink);
        faviconNodes.forEach(faviconNode => this.renderer.appendChild(document.head, faviconNode));
      }
    } else {
      this.logger.error('Failed to add favicon as <head> element is not accessible');
    }
  }

  private makeIconLink({rel, href, sizes}: IconLink): HTMLLinkElement {
    const link = this.renderer.createElement('link');
    link.rel = rel;
    link.href = href;
    if (sizes) {
      // sizes is a DOMTokenList attribute: assigning a string forwards to sizes.value, which sets the attribute
      link.sizes = sizes;
    }
    return link;
  }

  private makeTileImageMeta(): HTMLMetaElement {
    const meta = this.renderer.createElement('meta');
    meta.name = 'msapplication-TileImage';
    meta.content = this.TILE_IMAGE_URL;
    return meta;
  }
}
